import React, { useEffect, useState } from "react";

import { ReportType } from "api/model/enums";
import { DashboardEditorProps, GeneratedReportTableConfig } from "components/ComponentType";
import { IPeriodLoadingConfig } from "components/dashboard/product-template-table/utils/interfaces";
import { Chips } from "primereact/chips";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { T } from "services/lang/LanguageService";
import { DweenTimeUnitEnum } from "utils/time-utils";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";

const defaultLoadConf: IPeriodLoadingConfig = {
  defaultConfigUnit: DweenTimeUnitEnum.HOURS,
  truncateStartUnit: DweenTimeUnitEnum.DAYS,
  periodUnit: DweenTimeUnitEnum.DAYS,
  period: -1,
  offsetUnit: DweenTimeUnitEnum.HOURS,
  offset: 1,
};

const itemsPerPageOptions = [
  { label: "5", value: 5 },
  { label: "10", value: 10 },
  { label: "20", value: 20 },
  { label: "30", value: 30 },
];

const reportTypeOptions = Object.keys(ReportType).map(key => ({
  label: key,
  value: ReportType[key as keyof typeof ReportType],
}));

const chronoUnitOptions = Object.keys(DweenTimeUnitEnum).map(key => ({
  label: key,
  value: DweenTimeUnitEnum[key as keyof typeof DweenTimeUnitEnum],
}));

const GeneratedReportTableEditor: React.FC<DashboardEditorProps<GeneratedReportTableConfig>> = ({ config, onConfigChange }) => {
  const [currentConfig, setCurrentConfig] = useState(() => config);

  useEffect(() => {
    setCurrentConfig(config);
  }, [config]);

  const handleLoadConf = (e: InputSwitchChangeEvent) => {
    if (e.value) {
      onConfigChange({ ...currentConfig, loadingConfig: defaultLoadConf });
    } else {
      onConfigChange({ ...currentConfig, loadingConfig: undefined });
    }
  };

  const handleLoadConfChange = (e: InputNumberValueChangeEvent | DropdownChangeEvent) => {
    const key = e.target.id as keyof IPeriodLoadingConfig;
    const value = typeof e.value === "string" ? e.value.toUpperCase() : e.value;

    onConfigChange({
      ...currentConfig,
      loadingConfig: { ...currentConfig.loadingConfig, [key]: value },
    });
  };

  return (
    <div className="flex flex-column gap-3">
      <div className="flex flex-column gap-2">
        <label htmlFor="ids">IDs</label>
        <Chips
          id="ids"
          value={currentConfig.ids.map(String)}
          keyfilter="int"
          onChange={e => onConfigChange({ ...currentConfig, ids: e.value?.map(Number) ?? [] })}
        />
      </div>
      <div className="flex flex-row gap-5">
        <div className="flex flex-column w-2 gap-2">
          <label htmlFor="itemsPerPage">{T("component.items-per-page")}</label>
          <Dropdown
            value={currentConfig.itemsPerPage}
            options={itemsPerPageOptions}
            onChange={e => onConfigChange({ ...currentConfig, itemsPerPage: e.value })}
          />
        </div>
        <div className="flex flex-column w-2 gap-2">
          <label htmlFor="type">{T("word.type")}</label>
          <Dropdown
            value={currentConfig.type}
            options={reportTypeOptions}
            showClear
            onChange={(e: any) =>
              onConfigChange({
                ...currentConfig,
                type: e.value as ReportType,
              })
            }
          />
        </div>
        <div className="flex flex-column gap-3">
          <label htmlFor="showDateSelection">{T("word.date-selection")}</label>
          <InputSwitch
            checked={Boolean(currentConfig.showDateSelection)}
            onChange={e =>
              onConfigChange({
                ...currentConfig,
                showDateSelection: e.value,
              })
            }
          />
        </div>
        <div className="flex flex-column gap-3">
          <label htmlFor="showEmailButton">{T("word.email-button")}</label>
          <InputSwitch
            checked={Boolean(currentConfig.showEmailButton)}
            onChange={e =>
              onConfigChange({
                ...currentConfig,
                showEmailButton: e.value,
              })
            }
          />
        </div>
      </div>
      <div className="flex p-fluid">
        <InputLabelWrapper labelName={T("word.email-recipients")} col={6} className="p-0">
          <Chips
            value={currentConfig.emailRecipients}
            keyfilter="email"
            className="column-chip-display"
            placeholder={T("label.default-chip-placeholder")}
            onChange={e => onConfigChange({ ...currentConfig, emailRecipients: e.value ?? [] })}
          />
        </InputLabelWrapper>
      </div>
      <div>
        <div className="gap-2 flex flex-column justify-content-center align-items-start mb-3">
          <label htmlFor="loadConf">{T("load-conf.load-conf")}</label>
          <InputSwitch id="loadConf" checked={!!currentConfig.loadingConfig} onChange={handleLoadConf} />
        </div>
        {currentConfig.loadingConfig !== undefined && (
          <div className="flex flex-column gap-3">
            <div className="flex flex-row gap-3">
              <div className="flex flex-column gap-2">
                <label htmlFor="defaultConfigUnit">{T("load-conf.default-config-unit")}</label>
                <Dropdown
                  id="defaultConfigUnit"
                  value={currentConfig.loadingConfig.defaultConfigUnit}
                  options={chronoUnitOptions}
                  onChange={handleLoadConfChange}
                />
              </div>
              <div className="flex flex-column gap-2">
                <label htmlFor="truncateStartUnit">{T("load-conf.truncate-start-unit")}</label>
                <Dropdown
                  id="truncateStartUnit"
                  value={currentConfig.loadingConfig.truncateStartUnit}
                  options={chronoUnitOptions}
                  onChange={handleLoadConfChange}
                />
              </div>
              <div className="flex flex-column gap-2">
                <label htmlFor="periodUnit">{T("load-conf.period-unit")}</label>
                <Dropdown
                  id="periodUnit"
                  value={currentConfig.loadingConfig.periodUnit}
                  options={chronoUnitOptions}
                  onChange={handleLoadConfChange}
                />
              </div>
              <div className="flex flex-column gap-2">
                <label htmlFor="offsetUnit">{T("load-conf.offset-unit")}</label>
                <Dropdown
                  id="offsetUnit"
                  value={currentConfig.loadingConfig.offsetUnit}
                  options={chronoUnitOptions}
                  onChange={handleLoadConfChange}
                />
              </div>
            </div>
            <div className="flex flex-row gap-3">
              <div className="flex flex-column gap-2">
                <label htmlFor="period">{T("load-conf.period")}</label>
                <InputNumber
                  id="period"
                  value={currentConfig.loadingConfig.period as number}
                  onValueChange={handleLoadConfChange}
                />
              </div>
              <div className="flex flex-column gap-2">
                <label htmlFor="offset">{T("load-conf.offset")}</label>
                <InputNumber
                  id="offset"
                  value={currentConfig.loadingConfig.offset as number}
                  onValueChange={handleLoadConfChange}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneratedReportTableEditor;
